import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import viLocale from "@fullcalendar/core/locales/vi";
import enGbLocale from "@fullcalendar/core/locales/en-gb";
import { Pencil, X } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ResponsiveShell from "../components/layout/ResponsiveShell";
import { useLangPrefix } from "../hooks/useLangPrefix";
import useManagerStore from "../store/managerStore";
import useTenantVertical from "../hooks/useTenantVertical";
import { makeVT, isGuestVenueVertical } from "../utils/verticals";
import {
  canAccess,
  FULL_ACCESS,
  normalizeRole,
  RBAC_MODULES,
} from "../lib/rbac";
import * as bookingsData from "../lib/bookings/bookingsData";
import {
  BOOKING_FORM_LIMITS,
  buildBookingPayload,
  buildBookingUpdatePayload,
  getBookingEditForm,
  getEmptyBookingForm,
  validateBookingForm,
} from "../lib/bookings/bookingForm";
import {
  BOOKING_STATUS,
  DEFAULT_BOOKING_STATUSES,
  bookingStatusStyle,
  canConfirmBooking,
  isCancelledBooking,
} from "../lib/bookings/bookingStatus";
import { getCachedOrgTags, listOrgTags, peekCachedOrgTags } from "../api/tags";
import { getCachedCustomStatuses } from "../api/customStatuses";
import {
  DEFAULT_TAG_COLOR,
  enrichContactTags,
  getTagChipStyle,
  normalizeContactTags,
} from "../utils/contactTags";
import normalizeSearchText from "../utils/normalizeSearchText";
import {
  PHONE_INPUT_MAX_LENGTH,
  normalizePhoneNumber as normalizePhone,
  sanitizePhoneInput,
} from "../utils/phone";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const CALENDAR_TICKET_BG = "#e8ecfb";
const CALENDAR_TICKET_BORDER = "#c9d5ff";
const CALENDAR_TICKET_TEXT = "#1535c8";
const BOOKING_TAGS_INITIAL_LIMIT = 100;
const BOOKING_TAGS_SEARCH_LIMIT = 20;

function normalizeOrgTagOption(tag) {
  const name = String(tag?.name ?? tag?.label ?? "").trim();
  const id = String(tag?.id ?? tag?.tag_id ?? tag?.value ?? "").trim();
  if (!name || !id) return null;
  return {
    id,
    name,
    color: tag?.color ?? DEFAULT_TAG_COLOR,
  };
}

function mergeTagOptions(current, incoming) {
  const byId = new Map();
  current.forEach((tag) => {
    const option = normalizeOrgTagOption(tag);
    if (option) byId.set(option.id, option);
  });
  incoming.forEach((tag) => {
    const option = normalizeOrgTagOption(tag);
    if (option) byId.set(option.id, option);
  });
  return Array.from(byId.values());
}

function formatDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function getNearestListDateRow(calendarEl, targetDateStr) {
  const listEl = calendarEl?.querySelector(".fc-list");
  if (!listEl) return null;

  const exactRow =
    listEl.querySelector(`.fc-list-day[data-date="${targetDateStr}"]`) ||
    listEl.querySelector(`[data-date="${targetDateStr}"]`);
  if (exactRow) return exactRow;

  const dayRows = Array.from(
    listEl.querySelectorAll(".fc-list-day[data-date]"),
  );
  const rows = dayRows.length
    ? dayRows
    : Array.from(listEl.querySelectorAll("[data-date]"));
  const validRows = rows.filter((row) =>
    DATE_RE.test(row.getAttribute("data-date") || ""),
  );

  return (
    validRows.find(
      (row) => (row.getAttribute("data-date") || "") > targetDateStr,
    ) ||
    validRows[validRows.length - 1] ||
    null
  );
}

function formatDaySheetDate(dateStr, weekdaysFullArr) {
  const d = new Date(`${dateStr}T00:00:00`);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const weekday = weekdaysFullArr[d.getDay()];
  return `${weekday}, ${dd}/${mm}/${d.getFullYear()}`;
}

function addMinutes(dateTimeString, minutes) {
  const date = new Date(dateTimeString);
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
}

function statusTicketStyle(status) {
  return bookingStatusStyle(status).ticket;
}

function StatusActionContent({ isLoading, children }) {
  return (
    <span className="inline-flex items-center justify-center gap-2">
      {isLoading && (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden="true"
        />
      )}
      <span>{children}</span>
    </span>
  );
}

export default function BookingsPage() {
  const { t, i18n } = useTranslation();
  const { role, tenantId: storeTenantId } = useManagerStore();
  const tenantId = storeTenantId;
  const vertical = useTenantVertical();
  const vt = makeVT(t, vertical);
  const guestVenue = isGuestVenueVertical(vertical);
  const navigate = useNavigate();
  const { prefix } = useLangPrefix();
  const weekdaysFull = t("bookings.calendar.weekdaysFull", {
    returnObjects: true,
  });
  const today = new Date();
  const todayStr = formatDateStr(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const normalizedRole = normalizeRole(role);
  const canManageBookings = canAccess(
    normalizedRole,
    RBAC_MODULES.bookings,
    FULL_ACCESS,
  );
  const canFAB = canManageBookings;
  const calendarLocale = i18n.language?.startsWith("vi")
    ? viLocale
    : enGbLocale;
  const weekDayHeaderFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(
        i18n.language?.startsWith("vi") ? "vi-VN" : "en-GB",
        { weekday: "short" },
      ),
    [i18n.language],
  );
  const calendarRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const listTodayScrollTimeoutRef = useRef(null);
  const lastListTodayScrollKeyRef = useRef("");
  const pendingListTodayScrollKeyRef = useRef("");

  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [showDaySheet, setShowDaySheet] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [bookings, setBookings] = useState([]);
  const [bookingsLoadFailed, setBookingsLoadFailed] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetail, setShowBookingDetail] = useState(false);
  const [showBookingEditForm, setShowBookingEditForm] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  const [isUpdatingBooking, setIsUpdatingBooking] = useState(false);
  const [statusActionLoading, setStatusActionLoading] = useState(null);
  const statusActionLoadingRef = useRef(null);
  const [currentViewDate, setCurrentViewDate] = useState(today);
  const [isMobileCalendarView, setIsMobileCalendarView] = useState(
    () => window.matchMedia("(max-width: 767px)").matches,
  );
  const [form, setForm] = useState(getEmptyBookingForm);
  const [formErrors, setFormErrors] = useState({});
  const [editForm, setEditForm] = useState(getEmptyBookingForm);
  const [editFormErrors, setEditFormErrors] = useState({});
  const [bookingTagOptions, setBookingTagOptions] = useState([]);
  const [bookingOrgTags, setBookingOrgTags] = useState([]);
  const [bookingTagSearch, setBookingTagSearch] = useState("");
  const [isLoadingBookingTags, setIsLoadingBookingTags] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [customBookingStatuses, setCustomBookingStatuses] = useState([]);
  const currentMonthParam = `${currentViewDate.getFullYear()}-${String(
    currentViewDate.getMonth() + 1,
  ).padStart(2, "0")}`;

  useEffect(() => {
    if (!tenantId) {
      setCustomBookingStatuses([]);
      return;
    }
    let cancelled = false;
    getCachedCustomStatuses(tenantId, "booking")
      .then((items) => {
        if (!cancelled) {
          setCustomBookingStatuses(
            (Array.isArray(items) ? items : []).filter((s) => !s.is_default),
          );
        }
      })
      .catch((err) =>
        Sentry.captureException(err, {
          tags: { action: "fetch_booking_custom_statuses" },
        }),
      );
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const customBookingStatusBySlug = useMemo(() => {
    const map = {};
    customBookingStatuses.forEach((s) => {
      map[s.slug] = s;
    });
    return map;
  }, [customBookingStatuses]);

  function bookingStatusLabel(slug) {
    const custom = customBookingStatusBySlug[slug];
    if (custom) return custom.label;
    return vt(`bookings.bookingStatus.${slug}`, {
      defaultValue: t("bookings.bookingStatus.pending"),
    });
  }

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    const handleMobileQueryChange = (event) =>
      setIsMobileCalendarView(event.matches);

    setIsMobileCalendarView(mobileQuery.matches);
    mobileQuery.addEventListener("change", handleMobileQueryChange);

    return () => {
      mobileQuery.removeEventListener("change", handleMobileQueryChange);
    };
  }, []);

  useEffect(() => {
    if (!isMobileCalendarView) return;

    const api = calendarRef.current?.getApi?.();
    if (!api) return;

    const allowedMobileViews = ["timeGridDay", "listMonth"];
    if (!allowedMobileViews.includes(api.view.type)) {
      api.changeView("timeGridDay");
    }
  }, [isMobileCalendarView]);

  useEffect(() => {
    const calendarEl = calendarContainerRef.current;
    if (!calendarEl) return undefined;

    const updateCalendarSize = () => {
      const api = calendarRef.current?.getApi?.();
      api?.updateSize();
    };
    const scheduleUpdate = () => {
      window.requestAnimationFrame(updateCalendarSize);
    };

    scheduleUpdate();
    const transitionTimeouts = [80, 180, 260].map((delay) =>
      window.setTimeout(updateCalendarSize, delay),
    );
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleUpdate)
        : null;
    resizeObserver?.observe(calendarEl);
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      transitionTimeouts.forEach((id) => window.clearTimeout(id));
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [isMobileCalendarView]);

  useEffect(
    () => () => {
      if (listTodayScrollTimeoutRef.current) {
        window.clearTimeout(listTodayScrollTimeoutRef.current);
      }
    },
    [],
  );

  const calendarInitialView = isMobileCalendarView
    ? "timeGridDay"
    : "dayGridMonth";
  const calendarHeaderToolbar = isMobileCalendarView
    ? {
        left: "prev,next today",
        center: "title",
        right: "timeGridDay,listMonth",
      }
    : {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay,listMonth",
      };

  const loadBookings = useCallback(async () => {
    if (!tenantId) {
      setBookings([]);
      setBookingsLoadFailed(false);
      return [];
    }

    const result = await bookingsData.loadMonth({
      tenantId,
      month: currentMonthParam,
      fallbackDate: todayStr,
    });
    if (!result.ok) {
      Sentry.captureException(result.error, {
        tags: { action: "load_bookings" },
        extra: { tenantId, month: currentMonthParam },
      });
    }
    setBookingsLoadFailed(!result.ok);
    setBookings(result.bookings);
    return result.bookings;
  }, [tenantId, todayStr, currentMonthParam]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  useEffect(() => {
    if (!tenantId) {
      setBookingOrgTags([]);
      setBookingTagOptions([]);
      return undefined;
    }

    let cancelled = false;
    const applyOrgTags = (items) => {
      const options = mergeTagOptions([], items);
      setBookingOrgTags(options);
      setBookingTagOptions((prev) => mergeTagOptions(prev, options));
    };

    const cached = peekCachedOrgTags(tenantId);
    if (cached) {
      applyOrgTags(cached);
      return undefined;
    }

    getCachedOrgTags(tenantId)
      .then((items) => {
        if (!cancelled) applyOrgTags(items);
      })
      .catch((err) => {
        if (!cancelled) setBookingOrgTags([]);
        Sentry.captureException(err, {
          tags: { action: "load_booking_tag_colors" },
          extra: { tenantId },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!showBookingForm || !tenantId) return undefined;

    let cancelled = false;
    const search = bookingTagSearch.trim();
    const timeoutId = window.setTimeout(
      () => {
        setIsLoadingBookingTags(true);
        listOrgTags(tenantId, {
          ...(search ? { search } : {}),
          skip: 0,
          limit: search
            ? BOOKING_TAGS_SEARCH_LIMIT
            : BOOKING_TAGS_INITIAL_LIMIT,
        })
          .then((items) => {
            if (cancelled) return;
            setBookingTagOptions((prev) => mergeTagOptions(prev, items));
            setBookingOrgTags((prev) => mergeTagOptions(prev, items));
          })
          .catch((err) => {
            if (cancelled) return;
            Sentry.captureException(err, {
              tags: { action: "list_booking_contact_tags" },
              extra: { tenantId, search },
            });
          })
          .finally(() => {
            if (!cancelled) setIsLoadingBookingTags(false);
          });
      },
      search ? 250 : 0,
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [showBookingForm, tenantId, bookingTagSearch]);

  useEffect(() => {
    if (showBookingForm) return;
    setBookingTagSearch("");
  }, [showBookingForm]);

  const displayBookings = useMemo(
    () =>
      bookings.map((booking) => ({
        ...booking,
        contact_tags: enrichContactTags(booking.contact_tags, bookingOrgTags),
      })),
    [bookings, bookingOrgTags],
  );

  const filteredBookings = useMemo(
    () =>
      displayBookings.filter((booking) =>
        statusFilter === "all"
          ? !isCancelledBooking(booking.status)
          : booking.status === statusFilter,
      ),
    [displayBookings, statusFilter],
  );

  const monthBookings = displayBookings.filter((booking) =>
    booking.date.startsWith(currentMonthParam),
  );
  const statusCounts = monthBookings.reduce(
    (acc, booking) => {
      if (DEFAULT_BOOKING_STATUSES.includes(booking.status)) {
        acc[booking.status] += 1;
      } else {
        // A tenant's custom status: counted on its own, never folded into
        // a default one (that would misreport counts).
        acc.custom[booking.status] = (acc.custom[booking.status] || 0) + 1;
      }
      acc.all += 1;
      return acc;
    },
    {
      all: 0,
      ...Object.fromEntries(DEFAULT_BOOKING_STATUSES.map((status) => [status, 0])),
      custom: {},
    },
  );
  const statusFilterBadges = [
    {
      status: "all",
      label: t("bookings.bookingStatus.all"),
      count: statusCounts.all,
      style: {
        bg: "#f5f7ff",
        border: "#d9e2ff",
        text: "#1535c8",
      },
    },
    ...DEFAULT_BOOKING_STATUSES.map((status) => ({
      status,
      label: vt(`bookings.bookingStatus.${status}`),
      count: statusCounts[status],
      style: statusTicketStyle(status),
    })),
    ...Object.entries(statusCounts.custom).map(([status, count]) => ({
      status,
      label: bookingStatusLabel(status),
      count,
      style: statusTicketStyle(status),
    })),
  ];

  const calendarEvents = useMemo(
    () =>
      filteredBookings
        .filter((b) => !isCancelledBooking(b.status))
        .map((booking) => {
          const start = `${booking.date}T${booking.time_slot}:00`;
          return {
            id: booking.id,
            title: booking.customer_name,
            start,
            end: addMinutes(start, 60),
            extendedProps: {
              order_id: booking.order_id,
              customer_phone: booking.customer_phone,
              status: booking.status,
              guests: booking.guests,
            },
            borderColor: CALENDAR_TICKET_BORDER,
            backgroundColor: CALENDAR_TICKET_BG,
            textColor: CALENDAR_TICKET_TEXT,
          };
        }),
    [filteredBookings],
  );

  const dayBookings = useMemo(
    () => filteredBookings.filter((booking) => booking.date === selectedDate),
    [filteredBookings, selectedDate],
  );
  const selectedDateEvents = useMemo(
    () =>
      calendarEvents.filter((event) =>
        String(event.start).startsWith(selectedDate),
      ),
    [calendarEvents, selectedDate],
  );
  const daySheetTimeRange = useMemo(() => {
    if (selectedDateEvents.length === 0) {
      return {
        slotMinTime: "06:00:00",
        slotMaxTime: "23:00:00",
        scrollTime: "06:00:00",
      };
    }

    const eventHours = selectedDateEvents
      .map((event) => {
        const parsed = new Date(event.start);
        if (!Number.isNaN(parsed.getTime())) return parsed.getHours();
        const match = String(event.start).match(/T(\d{2}):/);
        return match ? Number(match[1]) : null;
      })
      .filter((hour) => hour !== null);

    if (eventHours.length === 0) {
      return {
        slotMinTime: "06:00:00",
        slotMaxTime: "23:00:00",
        scrollTime: "06:00:00",
      };
    }

    const earliestHour = Math.min(...eventHours);
    const latestHour = Math.max(...eventHours);
    const minHour = Math.max(0, earliestHour - 1);
    const maxHour = Math.min(24, latestHour + 2);
    const safeMaxHour =
      maxHour <= minHour ? Math.min(24, minHour + 2) : maxHour;

    return {
      slotMinTime: `${String(minHour).padStart(2, "0")}:00:00`,
      slotMaxTime: `${String(safeMaxHour).padStart(2, "0")}:00:00`,
      scrollTime: `${String(minHour).padStart(2, "0")}:00:00`,
    };
  }, [selectedDateEvents]);

  const isUpdatingBookingStatus = Boolean(statusActionLoading);
  const isStatusActionLoading = (id, status) =>
    statusActionLoading?.id === id && statusActionLoading?.status === status;
  const selectedBookingConfirmLoading = selectedBooking
    ? isStatusActionLoading(selectedBooking.id, "confirmed")
    : false;
  const selectedBookingCompleteLoading = selectedBooking
    ? isStatusActionLoading(selectedBooking.id, BOOKING_STATUS.completed)
    : false;
  const selectedBookingCancelLoading = selectedBooking
    ? isStatusActionLoading(selectedBooking.id, "cancelled")
    : false;
  const selectedBookingContactTags = useMemo(
    () => enrichContactTags(selectedBooking?.contact_tags, bookingOrgTags),
    [selectedBooking, bookingOrgTags],
  );
  const selectedBookingTagIds = useMemo(
    () => new Set((form.contactTags || []).map((tag) => tag.id)),
    [form.contactTags],
  );
  const normalizedBookingTagSearch = normalizeSearchText(bookingTagSearch);
  const availableBookingTagOptions = useMemo(
    () =>
      bookingTagOptions.filter((tag) => {
        if (!tag?.id || selectedBookingTagIds.has(tag.id)) return false;
        if (!normalizedBookingTagSearch) return true;
        return normalizeSearchText(tag.name).includes(
          normalizedBookingTagSearch,
        );
      }),
    [bookingTagOptions, normalizedBookingTagSearch, selectedBookingTagIds],
  );

  const scheduleListViewTodayScroll = useCallback(
    ({ force = false } = {}) => {
      const api = calendarRef.current?.getApi?.();
      const calendarEl = calendarContainerRef.current;
      if (!api || !calendarEl || api.view.type !== "listMonth") return;

      const viewStartStr = formatDateStr(
        api.view.currentStart.getFullYear(),
        api.view.currentStart.getMonth(),
        api.view.currentStart.getDate(),
      );
      const viewEndStr = formatDateStr(
        api.view.currentEnd.getFullYear(),
        api.view.currentEnd.getMonth(),
        api.view.currentEnd.getDate(),
      );
      if (todayStr < viewStartStr || todayStr >= viewEndStr) return;

      const scrollKey = [
        api.view.type,
        viewStartStr,
        viewEndStr,
        todayStr,
        statusFilter,
        calendarEvents.length,
      ].join(":");
      if (
        !force &&
        (lastListTodayScrollKeyRef.current === scrollKey ||
          pendingListTodayScrollKeyRef.current === scrollKey)
      ) {
        return;
      }

      if (listTodayScrollTimeoutRef.current) {
        window.clearTimeout(listTodayScrollTimeoutRef.current);
      }
      pendingListTodayScrollKeyRef.current = scrollKey;
      listTodayScrollTimeoutRef.current = window.setTimeout(() => {
        pendingListTodayScrollKeyRef.current = "";
        const targetRow = getNearestListDateRow(calendarEl, todayStr);
        if (!targetRow) return;

        lastListTodayScrollKeyRef.current = scrollKey;
        targetRow.scrollIntoView({ block: "start", behavior: "auto" });
      }, 80);
    },
    [calendarEvents.length, statusFilter, todayStr],
  );

  async function updateBookingStatus(id, status) {
    if (statusActionLoadingRef.current) return false;
    if (!tenantId) return false;

    const nextStatusAction = { id, status };
    statusActionLoadingRef.current = nextStatusAction;
    setStatusActionLoading(nextStatusAction);

    try {
      const result = await bookingsData.changeBookingStatus({
        tenantId,
        bookingId: id,
        status,
        month: currentMonthParam,
        bookings,
        fallbackDate: todayStr,
      });
      setBookings(result.bookings);
      setBookingsLoadFailed(false);
      if (result.booking) {
        setSelectedBooking((prev) =>
          prev?.id === id ? result.booking : prev,
        );
      }
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      showToastMessage(
        typeof detail === "string" ? detail : t("bookings.statusUpdateFailed"),
        "error",
      );
      Sentry.captureException(err, {
        tags: { action: "update_booking_status" },
        extra: { tenantId, bookingId: id, status },
      });
      return false;
    } finally {
      statusActionLoadingRef.current = null;
      setStatusActionLoading(null);
    }
  }

  function showToastMessage(message, type = "info") {
    if (type === "success") {
      toast.success(message);
      return;
    }
    if (type === "error") {
      toast.error(message);
      return;
    }
    toast(message);
  }

  function addExistingBookingTag(tag) {
    const option = normalizeOrgTagOption(tag);
    if (!option) return;
    setForm((prev) => {
      if ((prev.contactTags || []).some((item) => item.id === option.id)) {
        return prev;
      }
      return { ...prev, contactTags: [...(prev.contactTags || []), option] };
    });
    setFormErrors((prev) => ({ ...prev, phone: "" }));
    setBookingTagSearch("");
  }

  function removeExistingBookingTag(tagId) {
    setForm((prev) => ({
      ...prev,
      contactTags: (prev.contactTags || []).filter((tag) => tag.id !== tagId),
    }));
  }

  function commitBookingTagSearch() {
    const trimmed = bookingTagSearch.trim();
    if (!trimmed) return;
    const normalizedTrimmed = normalizeSearchText(trimmed);
    const exactAvailable = availableBookingTagOptions.find(
      (tag) => normalizeSearchText(tag.name) === normalizedTrimmed,
    );
    if (exactAvailable) {
      addExistingBookingTag(exactAvailable);
    }
  }

  function handleBookingTagSearchKeyDown(event) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    commitBookingTagSearch();
  }

  function handleBookingTicketClick(orderId, bookingId) {
    if (orderId) {
      navigate(`${prefix}/orders/${orderId}`);
      return;
    }
    const booking = filteredBookings.find((b) => b.id === bookingId);
    if (booking) {
      setSelectedBooking(booking);
      setShowBookingDetail(true);
    }
  }

  function openEditBooking(booking) {
    if (isUpdatingBooking || isUpdatingBookingStatus) return;
    setEditingBooking(booking);
    setEditForm(getBookingEditForm(booking, vertical));
    setEditFormErrors({});
    setShowBookingDetail(false);
    setShowBookingEditForm(true);
  }

  function closeBookingEditForm() {
    if (isUpdatingBooking) return;
    setShowBookingEditForm(false);
    setEditingBooking(null);
    setEditForm(getEmptyBookingForm(vertical));
    setEditFormErrors({});
  }

  async function handleUpdateBooking() {
    if (!tenantId || !editingBooking?.id) {
      showToastMessage(t("bookings.updateFailed"), "error");
      return;
    }

    const fallbackDate = editingBooking.date || selectedDate || todayStr;
    const { errors, normalized } = validateBookingForm(
      editForm,
      fallbackDate,
      t,
      {
        validateCustomer: false,
        requireGuests: guestVenue,
      },
    );
    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors);
      return;
    }
    setEditFormErrors({});

    const payload = buildBookingUpdatePayload(normalized);
    setIsUpdatingBooking(true);

    try {
      const { booking: updatedBooking, bookings: nextBookings } =
        await bookingsData.updateBookingDetails({
          tenantId,
          booking: editingBooking,
          payload,
          bookings,
          fallbackDate: todayStr,
        });
      setBookings(nextBookings);
      setSelectedBooking(updatedBooking);
      setSelectedDate(updatedBooking.date);
      setShowBookingEditForm(false);
      setEditingBooking(null);
      setEditForm(getEmptyBookingForm(vertical));
      setShowBookingDetail(true);
      showToastMessage(t("bookings.updateSuccess"), "success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errorMessage =
        typeof detail === "string" ? detail : t("bookings.updateFailed");
      Sentry.captureException(err, {
        tags: { action: "update_booking" },
        extra: { tenantId, bookingId: editingBooking.id, date: payload.date },
      });
      showToastMessage(errorMessage, "error");
    } finally {
      setIsUpdatingBooking(false);
    }
  }

  function findDuplicateBooking(phone, date, time) {
    return bookings.find(
      (booking) =>
        !isCancelledBooking(booking.status) &&
        normalizePhone(booking.customer_phone) === phone &&
        booking.date === date &&
        booking.time_slot === time,
    );
  }

  async function submitBooking(payload, dateStr, localContactTags = []) {
    setIsSavingBooking(true);
    try {
      const { bookings: nextBookings } = await bookingsData.createBooking({
        tenantId,
        payload,
        contactTags: localContactTags,
        bookings,
        fallbackDate: todayStr,
      });
      setBookings(nextBookings);
      setForm(getEmptyBookingForm(vertical));
      setFormErrors({});
      setShowBookingForm(false);
      setSelectedDate(dateStr);
      setShowDaySheet(true);
      showToastMessage(t("bookings.createSuccess"), "success");
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: "create_booking" },
        extra: { tenantId, date: dateStr },
      });
      showToastMessage(t("bookings.createFailed"), "error");
    } finally {
      setIsSavingBooking(false);
    }
  }

  async function handleSaveBooking({ forceDuplicate = false } = {}) {
    const fallbackDate = selectedDate || todayStr;
    const { errors, normalized } = validateBookingForm(form, fallbackDate, t, {
      requireGuests: guestVenue,
    });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    const dateStr = normalized.date;
    const payload = buildBookingPayload(normalized);
    const localContactTags = normalizeContactTags(form.contactTags);

    if (!forceDuplicate) {
      const duplicate = findDuplicateBooking(
        normalized.phone,
        normalized.date,
        normalized.time,
      );
      if (duplicate) {
        setDuplicateWarning({
          payload,
          date: normalized.date,
          time: normalized.time,
          duplicate,
          localContactTags,
        });
        return;
      }
    }

    await submitBooking(payload, dateStr, localContactTags);
  }

  function formatTicketTimeText(timeText) {
    const text = String(timeText || "").trim();
    if (!text) return "";
    if (!i18n.language?.startsWith("vi") && /^\d{1,2}$/.test(text)) {
      return `${text}h`;
    }
    return text;
  }

  function renderMonthEventContent(eventInfo) {
    const ticketStyle = statusTicketStyle(
      eventInfo.event.extendedProps.status,
    );
    const guests = eventInfo.event.extendedProps.guests;
    const timeText = formatTicketTimeText(eventInfo.timeText);
    return (
      <div
        className="text-[10px] leading-tight w-full rounded border px-1 py-0.5"
        style={{
          backgroundColor: ticketStyle.bg,
          borderColor: ticketStyle.border,
          color: ticketStyle.text,
        }}
      >
        <div className="font-semibold truncate">
          {timeText} • {eventInfo.event.title}
          {guestVenue && guests
            ? ` • ${t("bookings.ticketGuests", { count: guests })}`
            : ""}
        </div>
      </div>
    );
  }

  function renderDayEventContent(eventInfo) {
    const ticketStyle = statusTicketStyle(
      eventInfo.event.extendedProps.status,
    );
    const guests = eventInfo.event.extendedProps.guests;
    const timeText = formatTicketTimeText(eventInfo.timeText);
    return (
      <div
        className="text-[10px] leading-tight w-full rounded border px-1 py-0.5"
        style={{
          backgroundColor: ticketStyle.bg,
          borderColor: ticketStyle.border,
          color: ticketStyle.text,
        }}
      >
        <div className="font-semibold truncate">
          {timeText ? `${timeText} ` : ""}
          {eventInfo.event.title}
          {guestVenue && guests
            ? ` • ${t("bookings.ticketGuests", { count: guests })}`
            : ""}
        </div>
      </div>
    );
  }

  return (
    <ResponsiveShell title={vt("bookings.title")}>
      <div className="bg-[#f2f4f8]">
        <div className="mb-3 mt-1">
          <div className="bg-white rounded-xl p-3 shadow-sm border border-[#e3eaf8] overflow-x-auto">
            <div className="flex items-center gap-1.5 min-w-max">
              {statusFilterBadges.map((item) => {
                const selected = statusFilter === item.status;
                return (
                  <button
                    key={item.status}
                    onClick={() => setStatusFilter(item.status)}
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-medium transition"
                    style={{
                      backgroundColor: item.style.bg,
                      borderColor: selected ? "#1535c8" : item.style.border,
                      color: item.style.text,
                      boxShadow: selected ? "0 0 0 1px #1535c8 inset" : "none",
                    }}
                  >
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: item.style.text }}
                    />
                    <span className="font-semibold">({item.count})</span>
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {bookingsLoadFailed && (
          <div
            role="alert"
            className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-[#f5c2c8] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
          >
            <span>{t("bookings.loadFailed")}</span>
            <button
              type="button"
              className="shrink-0 rounded-lg border border-[#c62828] px-3 py-1 text-xs font-semibold"
              onClick={() => loadBookings()}
            >
              {t("common.retry")}
            </button>
          </div>
        )}

        <div ref={calendarContainerRef} className="card p-3 booking-calendar">
          <FullCalendar
            ref={calendarRef}
            plugins={[
              dayGridPlugin,
              timeGridPlugin,
              listPlugin,
              interactionPlugin,
            ]}
            locale={calendarLocale}
            initialView={calendarInitialView}
            firstDay={1}
            height="auto"
            events={calendarEvents}
            headerToolbar={calendarHeaderToolbar}
            buttonText={{
              today: t("bookings.today"),
              month: t("bookings.calendarViews.month"),
              week: t("bookings.calendarViews.week"),
              day: t("bookings.calendarViews.day"),
              list: t("bookings.calendarViews.list"),
            }}
            dayMaxEvents={2}
            dateClick={(info) => {
              setSelectedDate(info.dateStr);
              setShowDaySheet(true);
            }}
            eventClick={(info) => {
              info.jsEvent.preventDefault();
              handleBookingTicketClick(
                info.event.extendedProps.order_id,
                info.event.id,
              );
            }}
            datesSet={(info) => {
              setCurrentViewDate(info.view.currentStart);
              if (info.view.type !== "listMonth") {
                lastListTodayScrollKeyRef.current = "";
                pendingListTodayScrollKeyRef.current = "";
                return;
              }
              scheduleListViewTodayScroll({ force: true });
            }}
            eventsSet={() => {
              scheduleListViewTodayScroll();
            }}
            dayHeaderContent={(arg) =>
              arg.view.type === "timeGridWeek"
                ? weekDayHeaderFormatter.format(arg.date)
                : arg.text
            }
            eventContent={renderMonthEventContent}
          />
        </div>
      </div>

      {canFAB && (
        <button
          className="fixed bottom-24 right-4 w-14 h-14 bg-[#1535c8] rounded-full flex items-center justify-center shadow-lg z-20"
          onClick={() => setShowBookingForm(true)}
        >
          <span className="text-2xl font-light text-white">+</span>
        </button>
      )}

      {showDaySheet && selectedDate && (
        <>
          <div className="overlay" onClick={() => setShowDaySheet(false)} />
          <div
            className="bottom-sheet"
            style={{ maxHeight: "78vh", overflowY: "auto" }}
          >
            <div className="sheet-handle" />
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-base text-gray-900">
                {formatDaySheetDate(selectedDate, weekdaysFull)}
              </span>
              <button
                className="text-gray-400 text-xl leading-none"
                onClick={() => setShowDaySheet(false)}
              >
                ×
              </button>
            </div>

            <div className="card p-2 mb-3">
              <FullCalendar
                key={`day-${selectedDate}-${statusFilter}`}
                plugins={[timeGridPlugin]}
                locale={calendarLocale}
                initialView="timeGridDay"
                initialDate={selectedDate}
                allDaySlot={false}
                nowIndicator
                height={420}
                headerToolbar={false}
                slotMinTime={daySheetTimeRange.slotMinTime}
                slotMaxTime={daySheetTimeRange.slotMaxTime}
                scrollTime={daySheetTimeRange.scrollTime}
                events={selectedDateEvents}
                eventClick={(info) => {
                  info.jsEvent.preventDefault();
                  handleBookingTicketClick(
                    info.event.extendedProps.order_id,
                    info.event.id,
                  );
                }}
                eventContent={renderDayEventContent}
                eventTimeFormat={{
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                }}
              />
            </div>

            {dayBookings.length === 0 ? (
              <div className="py-4 text-center text-gray-400 text-sm">
                {t("bookings.noBookings")}
              </div>
            ) : (
              dayBookings.map((booking) => {
                const statusMeta = bookingStatusStyle(booking.status);
                const isConfirmActionLoading = isStatusActionLoading(
                  booking.id,
                  "confirmed",
                );
                const isCompleteActionLoading = isStatusActionLoading(
                  booking.id,
                  "completed",
                );
                const isCancelActionLoading = isStatusActionLoading(
                  booking.id,
                  "cancelled",
                );
                return (
                  <div
                    key={booking.id}
                    className="bg-[#f8f9ff] rounded-xl p-3 mb-3"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-bold text-gray-800">
                        #{booking.id}
                      </span>
                      <span
                        className={`pill ${statusMeta.bg} ${statusMeta.text}`}
                      >
                        {bookingStatusLabel(booking.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 mt-2 text-xs text-gray-600">
                      <div>
                        <span className="text-gray-400">
                          {t("bookings.guest")}{" "}
                        </span>
                        {booking.customer_name}
                      </div>
                      <div>
                        <span className="text-gray-400">
                          {t("bookings.time")}{" "}
                        </span>
                        {booking.time_slot}
                      </div>
                      {guestVenue && (
                        <div>
                          <span className="text-gray-400">
                            {t("bookings.guests")}{" "}
                          </span>
                          {booking.guests}
                        </div>
                      )}
                      {guestVenue && (
                        <div>
                          <span className="text-gray-400">
                            {t("bookings.table")}{" "}
                          </span>
                          {booking.table
                            ? `#${booking.table}`
                            : t("bookings.notAssigned")}
                        </div>
                      )}
                      {booking.service_ref && (
                        <div>
                          <span className="text-gray-400">
                            {vt("bookings.detailService")}{" "}
                          </span>
                          {booking.service_ref}
                        </div>
                      )}
                      {booking.staff_label && (
                        <div>
                          <span className="text-gray-400">
                            {vt("bookings.detailStaff")}{" "}
                          </span>
                          {booking.staff_label}
                        </div>
                      )}
                      <div>
                        <span className="text-gray-400">
                          {t("bookings.source")}{" "}
                        </span>
                        {booking.source || "-"}
                      </div>
                      <div>
                        <span className="text-gray-400">
                          {t("bookings.phone")}{" "}
                        </span>
                        {booking.customer_phone || "-"}
                      </div>
                    </div>
                    {booking.notes ? (
                      <div className="text-xs text-gray-500 mt-1">
                        {t("bookings.notes")} {booking.notes}
                      </div>
                    ) : null}
                    {booking.contact_tags?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {booking.contact_tags.map((tag) => {
                          const chipStyle = getTagChipStyle(tag, "detail");
                          return (
                            <span
                              key={tag.id || tag.slug || tag.name}
                              className={`max-w-full truncate rounded-full px-2 py-0.5 text-xs font-medium ${chipStyle.className || ""}`}
                              style={chipStyle.style}
                            >
                              {tag.name}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {canManageBookings && (
                        <button
                          className="inline-flex min-w-[82px] items-center justify-center border border-[#c9d5ff] text-[#1535c8] text-xs px-3 py-1.5 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                          onClick={() => openEditBooking(booking)}
                          disabled={
                            isUpdatingBooking || isUpdatingBookingStatus
                          }
                        >
                          {t("bookings.edit")}
                        </button>
                      )}
                      {canConfirmBooking(booking.status) && (
                        <button
                          className="inline-flex min-w-[82px] items-center justify-center bg-[#1535c8] text-white text-xs px-3 py-1.5 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                          onClick={() =>
                            updateBookingStatus(booking.id, "confirmed")
                          }
                          disabled={isUpdatingBookingStatus}
                        >
                          <StatusActionContent
                            isLoading={isConfirmActionLoading}
                          >
                            {t("bookings.confirm")}
                          </StatusActionContent>
                        </button>
                      )}
                      {booking.status === "confirmed" && (
                        <button
                          className="inline-flex min-w-[82px] items-center justify-center bg-[#1565c0] text-white text-xs px-3 py-1.5 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                          onClick={() =>
                            updateBookingStatus(booking.id, "completed")
                          }
                          disabled={isUpdatingBookingStatus}
                        >
                          <StatusActionContent
                            isLoading={isCompleteActionLoading}
                          >
                            {vt("bookings.complete")}
                          </StatusActionContent>
                        </button>
                      )}
                      {booking.status !== "cancelled" &&
                        booking.status !== "completed" && (
                          <button
                            className="inline-flex min-w-[112px] items-center justify-center border border-red-300 text-red-500 text-xs px-3 py-1.5 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                            onClick={() =>
                              updateBookingStatus(booking.id, "cancelled")
                            }
                            disabled={isUpdatingBookingStatus}
                          >
                            <StatusActionContent
                              isLoading={isCancelActionLoading}
                            >
                              {t("bookings.cancelBooking")}
                            </StatusActionContent>
                          </button>
                        )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {showBookingForm && (
        <>
          <div
            className="overlay"
            onClick={() => {
              setShowBookingForm(false);
              setDuplicateWarning(null);
            }}
          />
          <div
            className="bottom-sheet"
            style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}
          >
            <div className="sheet-handle" />
            <div className="flex items-center mb-4">
              <div className="w-6" />
              <span className="text-base font-bold text-gray-900 flex-1 text-center">
                {vt("bookings.bookingFormTitle")}
              </span>
              <button
                className="text-gray-400 text-xl leading-none px-1"
                onClick={() => {
                  setShowBookingForm(false);
                  setDuplicateWarning(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="flex flex-col gap-4 pb-2">
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.customerName")}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                  placeholder={t("bookings.customerNamePlaceholder")}
                  value={form.name}
                  maxLength={BOOKING_FORM_LIMITS.nameMaxLength}
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, name: event.target.value }));
                    setFormErrors((prev) => ({ ...prev, name: "" }));
                  }}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.phone")}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="tel"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                  placeholder={t("bookings.phonePlaceholder")}
                  value={form.phone}
                  inputMode="tel"
                  maxLength={PHONE_INPUT_MAX_LENGTH}
                  onChange={(event) => {
                    setForm((prev) => ({
                      ...prev,
                      phone: sanitizePhoneInput(event.target.value),
                    }));
                    setFormErrors((prev) => ({ ...prev, phone: "" }));
                  }}
                />
                {formErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {formErrors.phone}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.customerTags")}
                </label>
                {form.contactTags?.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {(form.contactTags || []).map((tag) => {
                      const chipStyle = getTagChipStyle(tag, "detail");
                      return (
                        <span
                          key={tag.id}
                          className={`inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${chipStyle.className || ""}`}
                          style={chipStyle.style}
                        >
                          <span className="truncate">{tag.name}</span>
                          <button
                            type="button"
                            className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full hover:bg-black/10"
                            onClick={() => removeExistingBookingTag(tag.id)}
                            aria-label={t("bookings.removeCustomerTag", {
                              name: tag.name,
                            })}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    className="min-w-0 flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                    placeholder={t("bookings.customerTagsPlaceholder")}
                    value={bookingTagSearch}
                    onChange={(event) =>
                      setBookingTagSearch(event.target.value)
                    }
                    onKeyDown={handleBookingTagSearchKeyDown}
                  />
                </div>
                <div className="mt-2 max-h-36 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50">
                  {isLoadingBookingTags && (
                    <div className="px-3 py-2 text-xs text-gray-500">
                      {t("contactTags.assigning")}
                    </div>
                  )}
                  {!isLoadingBookingTags &&
                    availableBookingTagOptions.length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-500">
                        {t("contactTags.noResults")}
                      </div>
                    )}
                  {!isLoadingBookingTags &&
                    availableBookingTagOptions.slice(0, 8).map((tag) => (
                      <button
                        key={tag.id}
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-white"
                        onClick={() => addExistingBookingTag(tag)}
                      >
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor: tag.color || DEFAULT_TAG_COLOR,
                          }}
                        />
                        <span className="truncate">{tag.name}</span>
                      </button>
                    ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.emailOptional")}
                </label>
                <input
                  type="email"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                  placeholder={t("bookings.emailPlaceholder")}
                  value={form.email}
                  onChange={(event) => {
                    setForm((prev) => ({ ...prev, email: event.target.value }));
                    setFormErrors((prev) => ({ ...prev, email: "" }));
                  }}
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {formErrors.email}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold text-gray-900 mb-1 block">
                    {t("bookings.bookingDate")}
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                    value={form.date || selectedDate}
                    onChange={(event) => {
                      setForm((prev) => ({
                        ...prev,
                        date: event.target.value,
                      }));
                      setFormErrors((prev) => ({ ...prev, date: "" }));
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {t("bookings.bookingDateHint")}
                  </p>
                  {formErrors.date && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.date}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-900 mb-1 block">
                    {t("bookings.bookingTime")}
                  </label>
                  <input
                    type="time"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                    value={form.time}
                    onChange={(event) => {
                      setForm((prev) => ({
                        ...prev,
                        time: event.target.value,
                      }));
                      setFormErrors((prev) => ({ ...prev, time: "" }));
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {t("bookings.bookingTimeHint")}
                  </p>
                  {formErrors.time && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.time}
                    </p>
                  )}
                </div>
              </div>
              {vertical !== "fnb" && (
                <>
                  <div>
                    <label className="text-sm font-bold text-gray-900 mb-1 block">
                      {vt("bookings.service")}
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                      placeholder={vt("bookings.servicePlaceholder")}
                      value={form.service}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          service: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-bold text-gray-900 mb-1 block">
                        {vt("bookings.durationMinutes")}
                      </label>
                      <input
                        type="number"
                        min={1}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                        placeholder={vt("bookings.durationPlaceholder")}
                        value={form.durationMinutes}
                        onChange={(event) => {
                          setForm((prev) => ({
                            ...prev,
                            durationMinutes: event.target.value,
                          }));
                          setFormErrors((prev) => ({ ...prev, durationMinutes: "" }));
                        }}
                      />
                      {formErrors.durationMinutes && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.durationMinutes}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-900 mb-1 block">
                        {vt("bookings.staff")}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                        placeholder={vt("bookings.staffPlaceholder")}
                        value={form.staff}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            staff: event.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </>
              )}
              {guestVenue && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold text-gray-900 mb-1 block">
                    {t("bookings.numberOfGuests")}
                  </label>
                  <input
                    type="number"
                    min={BOOKING_FORM_LIMITS.guestsMin}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                    value={form.guests}
                    onChange={(event) => {
                      setForm((prev) => ({
                        ...prev,
                        guests: event.target.value,
                      }));
                      setFormErrors((prev) => ({ ...prev, guests: "" }));
                    }}
                  />
                  {formErrors.guests && (
                    <p className="text-red-500 text-xs mt-1">
                      {formErrors.guests}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-900 mb-1 block">
                    {t("bookings.tableNumber")}
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                    placeholder={t("bookings.tablePlaceholder")}
                    value={form.table}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        table: event.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              )}
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.bookingNotes")}
                </label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8] resize-none"
                  placeholder={t("bookings.bookingNotesPlaceholder")}
                  value={form.notes}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      notes: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="sticky bottom-0 bg-white pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+4px)]">
                <button
                  className="w-full bg-[#1535c8] text-white rounded-xl py-4 text-base font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={handleSaveBooking}
                  disabled={isSavingBooking}
                >
                  {isSavingBooking
                    ? `${vt("bookings.createBooking")}...`
                    : vt("bookings.createBooking")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {showBookingEditForm && editingBooking && (
        <>
          <div className="overlay" onClick={closeBookingEditForm} />
          <div
            className="bottom-sheet"
            style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}
          >
            <div className="sheet-handle" />
            <div className="flex items-center mb-4">
              <div className="w-6" />
              <span className="text-base font-bold text-gray-900 flex-1 text-center">
                {vt("bookings.editBooking")}
              </span>
              <button
                className="inline-flex h-8 w-8 items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-60"
                onClick={closeBookingEditForm}
                disabled={isUpdatingBooking}
                aria-label={t("bookings.detail.close")}
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex flex-col gap-4 pb-2">
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.customerName")}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  className="w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 outline-none"
                  placeholder={t("bookings.customerNamePlaceholder")}
                  value={editForm.name}
                  maxLength={BOOKING_FORM_LIMITS.nameMaxLength}
                  readOnly
                  aria-readonly="true"
                />
                {editFormErrors.name && (
                  <p className="text-red-500 text-xs mt-1">
                    {editFormErrors.name}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.phone")}
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <input
                  type="tel"
                  className="w-full cursor-not-allowed rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500 outline-none"
                  placeholder={t("bookings.phonePlaceholder")}
                  value={editForm.phone}
                  inputMode="tel"
                  maxLength={PHONE_INPUT_MAX_LENGTH}
                  readOnly
                  aria-readonly="true"
                />
                {editFormErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {editFormErrors.phone}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-bold text-gray-900 mb-1 block">
                    {t("bookings.bookingDate")}
                  </label>
                  <input
                    type="date"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                    value={editForm.date || editingBooking.date}
                    onChange={(event) => {
                      setEditForm((prev) => ({
                        ...prev,
                        date: event.target.value,
                      }));
                      setEditFormErrors((prev) => ({ ...prev, date: "" }));
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {t("bookings.bookingDateHint")}
                  </p>
                  {editFormErrors.date && (
                    <p className="text-red-500 text-xs mt-1">
                      {editFormErrors.date}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-900 mb-1 block">
                    {t("bookings.bookingTime")}
                  </label>
                  <input
                    type="time"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                    value={editForm.time}
                    onChange={(event) => {
                      setEditForm((prev) => ({
                        ...prev,
                        time: event.target.value,
                      }));
                      setEditFormErrors((prev) => ({ ...prev, time: "" }));
                    }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {t("bookings.bookingTimeHint")}
                  </p>
                  {editFormErrors.time && (
                    <p className="text-red-500 text-xs mt-1">
                      {editFormErrors.time}
                    </p>
                  )}
                </div>
              </div>
              {vertical !== "fnb" && (
                <>
                  <div>
                    <label className="text-sm font-bold text-gray-900 mb-1 block">
                      {vt("bookings.service")}
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                      placeholder={vt("bookings.servicePlaceholder")}
                      value={editForm.service}
                      onChange={(event) =>
                        setEditForm((prev) => ({
                          ...prev,
                          service: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-bold text-gray-900 mb-1 block">
                        {vt("bookings.durationMinutes")}
                      </label>
                      <input
                        type="number"
                        min={1}
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                        placeholder={vt("bookings.durationPlaceholder")}
                        value={editForm.durationMinutes}
                        onChange={(event) => {
                          setEditForm((prev) => ({
                            ...prev,
                            durationMinutes: event.target.value,
                          }));
                          setEditFormErrors((prev) => ({ ...prev, durationMinutes: "" }));
                        }}
                      />
                      {editFormErrors.durationMinutes && (
                        <p className="text-red-500 text-xs mt-1">
                          {editFormErrors.durationMinutes}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-bold text-gray-900 mb-1 block">
                        {vt("bookings.staff")}
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                        placeholder={vt("bookings.staffPlaceholder")}
                        value={editForm.staff}
                        onChange={(event) =>
                          setEditForm((prev) => ({
                            ...prev,
                            staff: event.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                </>
              )}
              {guestVenue && (
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.numberOfGuests")}
                </label>
                <input
                  type="number"
                  min={BOOKING_FORM_LIMITS.guestsMin}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8]"
                  value={editForm.guests}
                  onChange={(event) => {
                    setEditForm((prev) => ({
                      ...prev,
                      guests: event.target.value,
                    }));
                    setEditFormErrors((prev) => ({ ...prev, guests: "" }));
                  }}
                />
                {editFormErrors.guests && (
                  <p className="text-red-500 text-xs mt-1">
                    {editFormErrors.guests}
                  </p>
                )}
              </div>
              )}
              <div>
                <label className="text-sm font-bold text-gray-900 mb-1 block">
                  {t("bookings.bookingNotes")}
                </label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#1535c8] resize-none"
                  placeholder={t("bookings.bookingNotesPlaceholder")}
                  value={editForm.notes}
                  onChange={(event) =>
                    setEditForm((prev) => ({
                      ...prev,
                      notes: event.target.value,
                    }))
                  }
                />
              </div>
              <div className="sticky bottom-0 bg-white pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+4px)] grid grid-cols-2 gap-2">
                <button
                  className="w-full border border-gray-300 text-gray-700 rounded-xl py-3 text-sm font-semibold disabled:opacity-60"
                  onClick={closeBookingEditForm}
                  disabled={isUpdatingBooking}
                >
                  {t("common.cancel")}
                </button>
                <button
                  className="w-full bg-[#1535c8] text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={handleUpdateBooking}
                  disabled={isUpdatingBooking}
                >
                  {isUpdatingBooking
                    ? t("common.saving")
                    : t("common.saveChanges")}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {duplicateWarning && (
        <>
          <div className="overlay" onClick={() => setDuplicateWarning(null)} />
          <div className="bottom-sheet">
            <div className="sheet-handle" />
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-base text-gray-900">
                {t("bookings.duplicateWarningTitle")}
              </span>
              <button
                className="text-gray-400 text-xl leading-none"
                onClick={() => setDuplicateWarning(null)}
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-600">
              {t("bookings.duplicateWarningDescription", {
                phone: duplicateWarning.payload.customer_phone,
                date: duplicateWarning.date,
                time: duplicateWarning.time,
              })}
            </p>
            <div className="mt-2 text-xs text-gray-500">
              {t("bookings.duplicateWarningExistingId", {
                id: duplicateWarning.duplicate.id,
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button
                className="border border-gray-300 text-gray-700 rounded-lg py-2 text-sm font-semibold"
                onClick={() => setDuplicateWarning(null)}
              >
                {t("bookings.duplicateWarningCancel")}
              </button>
              <button
                className="bg-[#1535c8] text-white rounded-lg py-2 text-sm font-semibold disabled:opacity-60"
                onClick={async () => {
                  const payload = duplicateWarning.payload;
                  const date = duplicateWarning.date;
                  const localContactTags = duplicateWarning.localContactTags;
                  setDuplicateWarning(null);
                  await submitBooking(payload, date, localContactTags);
                }}
                disabled={isSavingBooking}
              >
                {t("bookings.duplicateWarningConfirm")}
              </button>
            </div>
          </div>
        </>
      )}

      {showBookingDetail && selectedBooking && (
        <>
          <div
            className="overlay"
            onClick={() => setShowBookingDetail(false)}
          />
          <div className="bottom-sheet">
            <div className="sheet-handle" />
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-zinc-800">
                {vt("bookings.detail.title")}
              </h2>
              <div className="flex items-center gap-2">
                {canManageBookings && (
                  <button
                    type="button"
                    onClick={() => openEditBooking(selectedBooking)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-[#1535c8] disabled:cursor-not-allowed disabled:opacity-60"
                    aria-label={vt("bookings.editBooking")}
                    title={vt("bookings.editBooking")}
                    disabled={isUpdatingBooking || isUpdatingBookingStatus}
                  >
                    <Pencil size={16} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowBookingDetail(false)}
                  className="inline-flex h-8 w-8 items-center justify-center text-zinc-400 hover:text-zinc-600"
                  aria-label={t("bookings.detail.close")}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              {(selectedBooking.status === "confirmed" ||
                selectedBooking.status === "completed") && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">
                    {t("bookings.detail.bookingCode")}
                  </span>
                  <span className="font-medium text-zinc-800">
                    {selectedBooking.booking_code ||
                      t("bookings.detail.emptyValue")}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-500">
                  {t("bookings.detail.customer")}
                </span>
                <span className="font-medium text-zinc-800">
                  {selectedBooking.customer_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">
                  {t("bookings.detail.phone")}
                </span>
                <span className="font-medium text-zinc-800">
                  {selectedBooking.customer_phone ||
                    t("bookings.detail.emptyValue")}
                </span>
              </div>
              {selectedBookingContactTags.length > 0 && (
                <div className="flex justify-between gap-4">
                  <span className="text-zinc-500">
                    {t("bookings.customerTags")}
                  </span>
                  <div className="flex max-w-[60%] flex-wrap justify-end gap-1.5">
                    {selectedBookingContactTags.map((tag) => {
                      const chipStyle = getTagChipStyle(tag, "detail");
                      return (
                        <span
                          key={tag.id || tag.slug || tag.name}
                          className={`max-w-full truncate rounded-full px-2 py-0.5 text-xs font-medium ${chipStyle.className || ""}`}
                          style={chipStyle.style}
                        >
                          {tag.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-500">
                  {t("bookings.detail.date")}
                </span>
                <span className="font-medium text-zinc-800">
                  {selectedBooking.date}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">
                  {t("bookings.detail.time")}
                </span>
                <span className="font-medium text-zinc-800">
                  {selectedBooking.time_slot}
                </span>
              </div>
              {guestVenue && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">
                    {t("bookings.detail.guests")}
                  </span>
                  <span className="font-medium text-zinc-800">
                    {selectedBooking.guests}
                  </span>
                </div>
              )}
              {selectedBooking.service_ref && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">
                    {vt("bookings.detailService")}
                  </span>
                  <span className="font-medium text-zinc-800">
                    {selectedBooking.service_ref}
                  </span>
                </div>
              )}
              {selectedBooking.duration_minutes != null && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">
                    {vt("bookings.detailDuration")}
                  </span>
                  <span className="font-medium text-zinc-800">
                    {selectedBooking.duration_minutes}
                  </span>
                </div>
              )}
              {selectedBooking.staff_label && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">
                    {vt("bookings.detailStaff")}
                  </span>
                  <span className="font-medium text-zinc-800">
                    {selectedBooking.staff_label}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-zinc-500">
                  {t("bookings.detail.status")}
                </span>
                <span
                  className={`font-medium ${
                    bookingStatusStyle(selectedBooking.status).text
                  }`}
                >
                  {bookingStatusLabel(selectedBooking.status)}
                </span>
              </div>
              {selectedBooking.notes && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">
                    {t("bookings.detail.notes")}
                  </span>
                  <span className="font-medium text-zinc-800 text-right max-w-[60%]">
                    {selectedBooking.notes}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-6 flex flex-col gap-2">
              {canConfirmBooking(selectedBooking.status) && (
                <button
                  onClick={async () => {
                    await updateBookingStatus(selectedBooking.id, "confirmed");
                  }}
                  className="w-full bg-primary text-white rounded-xl py-3 font-medium hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isUpdatingBookingStatus}
                >
                  <StatusActionContent
                    isLoading={selectedBookingConfirmLoading}
                  >
                    {t("bookings.confirm")}
                  </StatusActionContent>
                </button>
              )}
              {selectedBooking.status === "confirmed" && (
                <button
                  onClick={async () => {
                    await updateBookingStatus(selectedBooking.id, "completed");
                  }}
                  className="w-full bg-[#1565c0] text-white rounded-xl py-3 font-medium hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isUpdatingBookingStatus}
                >
                  <StatusActionContent
                    isLoading={selectedBookingCompleteLoading}
                  >
                    {vt("bookings.complete")}
                  </StatusActionContent>
                </button>
              )}
              {selectedBooking.status !== "cancelled" &&
                selectedBooking.status !== "completed" && (
                  <button
                    onClick={async () => {
                      await updateBookingStatus(
                        selectedBooking.id,
                        "cancelled",
                      );
                      setShowBookingDetail(false);
                    }}
                    className="w-full border border-red-300 text-red-500 rounded-xl py-3 font-medium hover:bg-red-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={isUpdatingBookingStatus}
                  >
                    <StatusActionContent
                      isLoading={selectedBookingCancelLoading}
                    >
                      {vt("bookings.detail.cancelBooking")}
                    </StatusActionContent>
                  </button>
                )}
              {customBookingStatuses.length > 0 && (
                <select
                  value=""
                  disabled={isUpdatingBookingStatus}
                  onChange={async (e) => {
                    const slug = e.target.value;
                    if (!slug) return;
                    await updateBookingStatus(selectedBooking.id, slug);
                  }}
                  className="w-full rounded-xl border border-zinc-200 py-3 text-sm text-zinc-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">{t("bookings.detail.moreStatus")}</option>
                  {customBookingStatuses.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </>
      )}

      <style>{`
        .booking-calendar {
          min-width: 0;
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }
        .booking-calendar .fc {
          min-width: 0;
          width: 100%;
          max-width: 100%;
        }
        .booking-calendar .fc .fc-view-harness,
        .booking-calendar .fc .fc-view,
        .booking-calendar .fc .fc-scrollgrid {
          max-width: 100%;
        }
        .booking-calendar .fc .fc-toolbar-chunk {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .booking-calendar .fc .fc-button-primary {
          background: linear-gradient(135deg, #1f4de2 0%, #1535c8 100%);
          border: 1px solid #1535c8;
          color: #ffffff;
          text-transform: lowercase;
          font-weight: 600;
        }
        .booking-calendar .fc .fc-button-primary:hover,
        .booking-calendar .fc .fc-button-primary:focus {
          background: linear-gradient(135deg, #2459ff 0%, #1840d6 100%);
          border-color: #1840d6;
          box-shadow: none;
        }
        .booking-calendar .fc .fc-button-primary:not(:disabled).fc-button-active,
        .booking-calendar .fc .fc-button-primary:not(:disabled):active {
          background: linear-gradient(135deg, #132f9f 0%, #102887 100%);
          border-color: #102887;
        }
        .booking-calendar .fc-list-day {
          scroll-margin-top: 96px;
        }
        @media (max-width: 767px) {
          .booking-calendar .fc .fc-header-toolbar {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }
          .booking-calendar .fc .fc-toolbar-chunk {
            justify-content: center;
            flex-wrap: wrap;
          }
          .booking-calendar .fc .fc-toolbar-title {
            font-size: 1.9rem;
            text-align: center;
            line-height: 1.1;
          }
          .booking-calendar .fc .fc-toolbar-chunk:last-child {
            justify-content: flex-start;
            flex-wrap: nowrap;
            overflow-x: auto;
            padding-bottom: 2px;
            scrollbar-width: none;
            -webkit-overflow-scrolling: touch;
          }
          .booking-calendar .fc .fc-toolbar-chunk:last-child::-webkit-scrollbar {
            display: none;
          }
          .booking-calendar .fc .fc-button {
            white-space: nowrap;
          }
        }
      `}</style>
    </ResponsiveShell>
  );
}
